import { PatientLayout } from "@/components/layout/patient-layout";
import { useListOpenaiConversations, useCreateOpenaiConversation, useGetOpenaiConversation } from "@workspace/api-client-react";
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Loader2, Sparkles, PlusCircle, AlertTriangle, Clock, X, ShieldCheck, ShieldAlert } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { getGetOpenaiConversationQueryKey, getListOpenaiConversationsQueryKey } from "@workspace/api-client-react";
import { AiChatDisclaimer } from "@/components/ui/ai-chat-disclaimer";
import { AiConsentModal } from "@/components/ui/ai-consent-modal";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";
import { useOldestChatDeletionCountdown } from "@/lib/retention";
import { Link } from "wouter";

const PRIVACY_NOTICE_KEY = "rheumhub_patient_chat_privacy_notice_v1";

function PrivacyNoticeBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(PRIVACY_NOTICE_KEY);
    if (!dismissed) setShow(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(PRIVACY_NOTICE_KEY, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="flex items-start gap-3 bg-rose-50 border border-rose-100 rounded-2xl px-4 py-3 text-sm text-rose-800">
      <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-rose-400" />
      <p className="flex-1 leading-relaxed">
        Your conversations are automatically deleted after 90 days.{" "}
        <Link href="/patient/settings" className="underline font-medium hover:text-rose-900">
          Learn more in your privacy settings.
        </Link>
      </p>
      <button
        onClick={dismiss}
        className="shrink-0 text-rose-400 hover:text-rose-600 ml-1"
        aria-label="Dismiss privacy notice"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function RetentionSidebarNote() {
  const countdown = useOldestChatDeletionCountdown();
  if (!countdown) return null;
  return (
    <div
      className="flex items-start gap-2 px-4 py-3 border-b border-slate-100"
      title={`Scheduled deletion: ${countdown.absoluteLabel}`}
    >
      <Clock className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />
      <p className="text-xs text-slate-500 leading-snug">
        Your oldest chat will be deleted{" "}
        <span className="font-medium text-slate-700">{countdown.relativePhrase}</span>
        <span className="block text-[11px] text-slate-400 mt-0.5">{countdown.absoluteLabel}</span>
      </p>
    </div>
  );
}

export default function PatientChat() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [consented, setConsented] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: convos, isLoading: convosLoading } = useListOpenaiConversations();
  const createConvo = useCreateOpenaiConversation();
  const { data: activeConvo, isLoading: chatLoading } = useGetOpenaiConversation(
    activeId!, 
    { query: { enabled: !!activeId, queryKey: getGetOpenaiConversationQueryKey(activeId!) } }
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [activeConvo?.messages, isStreaming]);

  const handleStartNew = async () => {
    createConvo.mutate({ data: { title: "New Chat", userRole: "patient" } }, {
      onSuccess: (newConvo) => {
        setActiveId(newConvo.id);
        queryClient.invalidateQueries({ queryKey: getListOpenaiConversationsQueryKey() });
      }
    });
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !activeId || isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    setIsStreaming(true);
    setRateLimited(false);

    const tempUserMsg = { id: Date.now(), conversationId: activeId, role: "user", content: userMessage, createdAt: new Date().toISOString() };
    const tempAssistantMsg = { id: Date.now() + 1, conversationId: activeId, role: "assistant", content: "", createdAt: new Date().toISOString() };
    
    queryClient.setQueryData(getGetOpenaiConversationQueryKey(activeId), (old: any) => {
      if (!old) return old;
      return { ...old, messages: [...old.messages, tempUserMsg, tempAssistantMsg] };
    });

    try {
      const response = await fetch(`/api/openai/conversations/${activeId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: userMessage, userRole: "patient" })
      });

      if (response.status === 429) {
        setRateLimited(true);
        queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
        return;
      }

      if (!response.body) throw new Error("No response body");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const dataStr = line.slice(6);
            if (!dataStr) continue;
            try {
              const data = JSON.parse(dataStr);
              if (data.done) break;
              if (data.content) {
                assistantContent += data.content;
                queryClient.setQueryData(getGetOpenaiConversationQueryKey(activeId), (old: any) => {
                  if (!old) return old;
                  const msgs = [...old.messages];
                  msgs[msgs.length - 1].content = assistantContent;
                  return { ...old, messages: msgs };
                });
              }
            } catch (_e) {}
          }
        }
      }
    } catch (_error) {
    } finally {
      setIsStreaming(false);
      queryClient.invalidateQueries({ queryKey: getGetOpenaiConversationQueryKey(activeId) });
    }
  };

  return (
    <PatientLayout title="Your AI Guide">
      {!consented && (
        <AiConsentModal variant="patient" onConsented={() => setConsented(true)} />
      )}

      <div className="space-y-3">
        <PrivacyNoticeBanner />
        <AiChatDisclaimer variant="patient" />

        <div className="flex flex-col md:flex-row h-[calc(100vh-280px)] bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Sidebar */}
          <div className={`${activeId ? 'hidden md:flex' : 'flex'} w-full md:w-64 border-r border-slate-100 bg-slate-50/50 flex-col shrink-0`}>
            <div className="p-4 sm:p-6 border-b border-slate-100">
              <Button 
                onClick={handleStartNew} 
                className="w-full bg-rose-50 text-rose-600 hover:bg-rose-100 hover:text-rose-700 border-0 shadow-none rounded-xl h-12" 
                disabled={createConvo.isPending}
              >
                {createConvo.isPending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <PlusCircle className="h-5 w-5 mr-2" />}
                Start new chat
              </Button>
            </div>
            <RetentionSidebarNote />
            <div className="px-4 py-3 border-b border-slate-100">
              <Link
                href="/patient/safeguarding"
                className="flex items-center gap-2 w-full text-xs font-medium text-rose-600 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 rounded-xl px-3 py-2 transition-colors"
              >
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                Need urgent help?
              </Link>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {convosLoading ? (
                <div className="text-center text-sm text-slate-500 mt-4">Loading...</div>
              ) : convos?.length === 0 ? (
                <div className="text-center text-sm text-slate-500 mt-4 px-4">Start a chat to ask questions about your condition.</div>
              ) : (
                convos?.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm truncate transition-all ${
                      activeId === c.id 
                        ? "bg-white shadow-sm border border-slate-200 text-slate-900 font-medium" 
                        : "text-slate-600 hover:bg-white hover:text-slate-900 border border-transparent"
                    }`}
                  >
                    {c.title}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className={`${!activeId ? 'hidden md:flex' : 'flex'} flex-1 flex-col relative`}>
            {!activeId ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-white">
                <div className="h-16 w-16 bg-rose-50 text-rose-400 rounded-full flex items-center justify-center mb-6">
                  <Sparkles className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Hello, I'm here to help</h3>
                <p className="text-slate-600 max-w-md text-lg">
                  I can answer questions about your condition, explain medical terms, and point you toward helpful UK resources.
                </p>
                <Button 
                  onClick={handleStartNew} 
                  className="mt-8 bg-rose-500 hover:bg-rose-600 text-white rounded-full px-8 py-6 text-lg"
                >
                  Start a conversation
                </Button>
              </div>
            ) : (
              <>
                <div className="md:hidden p-4 border-b border-slate-100 bg-white flex items-center">
                  <Button variant="ghost" size="sm" onClick={() => setActiveId(null)} className="text-slate-500">
                    ← Back to chats
                  </Button>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-8 space-y-6" aria-live="polite" aria-label="Conversation messages" role="log">
                  {chatLoading ? (
                    <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin text-rose-400" /></div>
                  ) : activeConvo?.messages?.length === 0 ? (
                    <div className="flex flex-col items-center mt-10 px-2 sm:px-8 gap-6">
                      <div className="text-center">
                        <div className="inline-flex h-12 w-12 bg-rose-50 text-rose-400 rounded-full items-center justify-center mb-4">
                          <Sparkles className="h-6 w-6" />
                        </div>
                        <p className="text-slate-500 text-lg">What would you like to know today?</p>
                      </div>
                      <div className="w-full max-w-md bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 flex items-start gap-3">
                        <ShieldCheck className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
                        <div className="text-sm text-slate-600 leading-relaxed">
                          <p className="font-medium text-slate-700 mb-1">Your privacy matters</p>
                          <p>
                            Everything you share in this chat is private. Conversations are automatically deleted after your chosen retention period (default 90 days).{" "}
                            <Link href="/patient/settings" className="underline text-slate-700 font-medium hover:text-slate-900">
                              Manage in settings.
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    activeConvo?.messages?.map((msg, i) => (
                      <div key={msg.id || i} className={`flex gap-3 sm:gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'assistant' && (
                          <div className="h-10 w-10 rounded-full bg-rose-50 border border-rose-100 text-rose-500 flex items-center justify-center shrink-0">
                            <Sparkles className="h-5 w-5" />
                          </div>
                        )}
                        <div className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-5 py-4 ${
                          msg.role === 'user' 
                            ? 'bg-slate-800 text-white rounded-tr-sm' 
                            : 'bg-white border border-slate-200 shadow-sm text-slate-800 rounded-tl-sm'
                        }`}>
                          <div className="whitespace-pre-wrap leading-relaxed text-base">
                            {msg.content || (msg.role === 'assistant' && isStreaming ? '...' : '')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-4 sm:p-6 bg-white border-t border-slate-100 space-y-2">
                  {rateLimited && (
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      You've reached the message limit (30 per hour). Please try again a little later.
                    </div>
                  )}
                  <form onSubmit={handleSend} className="flex gap-3 max-w-4xl mx-auto relative">
                    <Input
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Type or dictate your question..."
                      className="flex-1 rounded-full h-14 pl-6 pr-24 bg-slate-50 border-slate-200 focus-visible:ring-rose-500 text-base"
                      disabled={isStreaming}
                    />
                    <VoiceInputButton
                      tone="rose"
                      size="md"
                      onTranscript={(chunk) => setInput((prev) => appendVoiceChunk(prev, chunk))}
                      disabled={isStreaming}
                      label="Dictate question"
                      className="absolute right-14 top-2"
                    />
                    <Button 
                      type="submit" 
                      disabled={!input.trim() || isStreaming} 
                      className="absolute right-2 top-2 h-10 w-10 rounded-full bg-rose-500 hover:bg-rose-600 p-0"
                    >
                      <Send className="h-4 w-4 text-white" />
                    </Button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PatientLayout>
  );
}
